import React, { useState, useEffect } from 'react';
import { 
  Building, Plus, Search, Edit, Trash2, CheckCircle, XCircle, 
  TrendingUp, Calendar, Globe, Phone, Mail, MapPin,
  Upload, Image, BarChart3, DollarSign, Activity,
  AlertTriangle, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { bankApi, Bank, BankSearchParams, InterestRate } from '@/api/banks';
import toast from 'react-hot-toast';
import FileUpload from '@/components/shared/FileUpload';
import { apiClient } from '@/api/client';
import type { BankStats } from '@/api/banks';

const BankManagement = () => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [interestRates, setInterestRates] = useState<InterestRate[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [showCreateRateModal, setShowCreateRateModal] = useState(false);
  const [showEditRateModal, setShowEditRateModal] = useState(false);
  const [editingRate, setEditingRate] = useState<InterestRate | null>(null);
  const [isCreatingRate, setIsCreatingRate] = useState(false);
  const [isUpdatingRate, setIsUpdatingRate] = useState(false);
  const [showEditBankModal, setShowEditBankModal] = useState(false);
  const [editingBank, setEditingBank] = useState<Bank | null>(null);
  const [isUpdatingBank, setIsUpdatingBank] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBanks, setTotalBanks] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const banksPerPage = 12;

  // Stats state
  const [stats, setStats] = useState<BankStats | null>(null);

  // Helpers: normalize file URLs
  const getApiOrigin = (): string => {
    try {
      const base = (apiClient.defaults.baseURL as string) || 'https://skyview-assets.de';
      return new URL(base).origin;
    } catch {
      return 'https://skyview-assets.de';
    }
  };

  const toBackendFileUrl = (url?: string): string | undefined => {
    if (!url) return url;
    const apiOrigin = getApiOrigin();
    try {
      const isAbsolute = /^https?:\/\//i.test(url);
      if (!isAbsolute) {
        // relative like /files/...
        // also allow /api/v1/files/... to be fetched from same origin
        if (url.startsWith('/api/')) return `${apiOrigin}${url}`;
        return `${apiOrigin}${url}`;
      }
      const given = new URL(url);
      // If absolute but not pointing to backend, rewrite only for /files paths
      if (given.origin !== apiOrigin && (given.pathname.startsWith('/files') || given.pathname.startsWith('/api/'))) {
        return `${apiOrigin}${given.pathname}`;
      }
      return url;
    } catch {
      return url;
    }
  };

  const toRelativeFilesPath = (url?: string): string | undefined => {
    if (!url) return url;
    try {
      const isAbsolute = /^https?:\/\//i.test(url);
      if (!isAbsolute) return url; // already relative
      const u = new URL(url);
      return u.pathname.startsWith('/files') ? u.pathname : url;
    } catch {
      return url;
    }
  };
 
  // Logo upload state
  const [createLogoUrl, setCreateLogoUrl] = useState<string | undefined>(undefined);
  const [editLogoUrl, setEditLogoUrl] = useState<string | undefined>(undefined);

  // Fetch interest rates for a specific bank
  const fetchInterestRates = async (bankId: number) => {
    try {
      const rates = await bankApi.rates.listByBank(bankId);
      setInterestRates(rates);
    } catch (err: any) {
      const errorMessage = err.message || 'Faiz oranları yüklenirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error fetching interest rates:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadBanks = async () => {
      setIsLoading(true);
      try {
        setError(null);
        const defaultParams: BankSearchParams = {
          page: currentPage,
          page_size: banksPerPage,
        };

        // Add search filter
        if (searchTerm.trim()) {
          defaultParams.search = searchTerm.trim();
        }

        const response = await bankApi.list(defaultParams);
        // fetch stats in parallel
        try {
          const statsResp = await bankApi.getStats();
          setStats(statsResp);
        } catch (e) {
          // ignore stats error but log
          console.warn('Bank stats fetch failed', e);
        }

        // Normalize logo URLs to backend origin for display
        setBanks(response.banks.map(b => ({ ...b, logo_url: toBackendFileUrl(b.logo_url) })));
        setTotalBanks(response.total);
        setTotalPages(Math.ceil(response.total / banksPerPage));
        
      } catch (err: any) {
        const errorMessage = err.message || 'Bankalar yüklenirken hata oluştu';
        setError(errorMessage);
        toast.error(errorMessage);
        console.error('Error fetching banks:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadBanks();
  }, [currentPage, searchTerm, banksPerPage]);

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      setError(null);
      const defaultParams: BankSearchParams = {
        page: currentPage,
        page_size: banksPerPage,
      };

      // Add search filter
      if (searchTerm.trim()) {
        defaultParams.search = searchTerm.trim();
      }

      const [response, statsResp] = await Promise.all([
        bankApi.list(defaultParams),
        bankApi.getStats(),
      ]);
      
      setBanks(response.banks.map(b => ({ ...b, logo_url: toBackendFileUrl(b.logo_url) })));
      setTotalBanks(response.total);
      setTotalPages(Math.ceil(response.total / banksPerPage));
      setStats(statsResp);
      
      toast.success('Veriler güncellendi');
    } catch (err: any) {
      const errorMessage = err.message || 'Bankalar yüklenirken hata oluştu';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching banks:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Toggle bank status (activate/deactivate)
  const toggleBankStatus = async (bankId: number) => {
    try {
      const updatedBank = await bankApi.toggleStatus(bankId);
      
      // Update local state
      setBanks(prev => prev.map(bank => 
        bank.id === bankId ? updatedBank : bank
      ));
      
      toast.success('Banka durumu güncellendi');
      
    } catch (err: any) {
      const errorMessage = err.message || 'Banka durumu güncellenirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error toggling bank status:', err);
    }
  };

  // Delete bank
  const deleteBank = async (bankId: number) => {
    if (!window.confirm('Bu bankayı silmek istediğinizden emin misiniz? Tüm ilişkili faiz oranları da silinecektir.')) {
      return;
    }

    try {
      await bankApi.delete(bankId);
      
      // Update local state
      setBanks(prev => prev.filter(bank => bank.id !== bankId));
      setTotalBanks(prev => prev - 1);
      
      toast.success('Banka silindi');
      
    } catch (err: any) {
      const errorMessage = err.message || 'Banka silinirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error deleting bank:', err);
    }
  };

  // View interest rates for a bank
  const viewInterestRates = async (bank: Bank) => {
    setSelectedBank(bank);
    setShowRatesModal(true);
    await fetchInterestRates(bank.id);
  };

  // Create new bank
  const createBank = async (bankData: { name: string; iban: string; description?: string; website?: string; contact_info?: string; logo_url?: string }) => {
    setIsCreating(true);
    try {
      // Persist relative path, display absolute
      const payload = { ...bankData, logo_url: toRelativeFilesPath(bankData.logo_url) };
      const newBank = await bankApi.create(payload);
      const newBankWithLogo = { ...newBank, logo_url: toBackendFileUrl(newBank.logo_url || bankData.logo_url) } as Bank;
      setBanks(prev => [newBankWithLogo, ...prev]);
      setTotalBanks(prev => prev + 1);
      toast.success('Banka başarıyla oluşturuldu');
      setShowCreateModal(false);
      setCreateLogoUrl(undefined);
    } catch (err: any) {
      const errorMessage = err.message || 'Banka oluşturulurken hata oluştu';
      toast.error(errorMessage);
      console.error('Error creating bank:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Show reports modal
  const showReports = () => {
    setShowReportsModal(true);
  };

  // Create new interest rate
  const createInterestRate = async (rateData: { term_days: number; rate: number; min_amount: number; max_amount?: number }) => {
    if (!selectedBank) return;
    
    setIsCreatingRate(true);
    try {
      const newRate = await bankApi.rates.create({
        bank_id: selectedBank.id,
        effective_date: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
        ...rateData
      });
      setInterestRates(prev => [newRate, ...prev]);
      toast.success('Faiz oranı başarıyla oluşturuldu');
      setShowCreateRateModal(false);
    } catch (err: any) {
      const errorMessage = err.message || 'Faiz oranı oluşturulurken hata oluştu';
      toast.error(errorMessage);
      console.error('Error creating interest rate:', err);
    } finally {
      setIsCreatingRate(false);
    }
  };

  // Toggle rate status
  const toggleRateStatus = async (rateId: number) => {
    try {
      const currentRate = interestRates.find(rate => rate.id === rateId);
      if (!currentRate) return;
      
      const updatedRate = await bankApi.rates.update(rateId, {
        is_active: !currentRate.is_active
      });
      setInterestRates(prev => prev.map(rate => 
        rate.id === rateId ? updatedRate : rate
      ));
      toast.success('Faiz oranı durumu güncellendi');
    } catch (err: any) {
      const errorMessage = err.message || 'Faiz oranı durumu güncellenirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error toggling rate status:', err);
    }
  };

  // Delete interest rate
  const deleteInterestRate = async (rateId: number) => {
    if (!window.confirm('Bu faiz oranını silmek istediğinizden emin misiniz?')) return;
    try {
      await bankApi.rates.delete(rateId);
      setInterestRates(prev => prev.filter(rate => rate.id !== rateId));
      toast.success('Faiz oranı silindi');
    } catch (err: any) {
      const errorMessage = err.message || 'Faiz oranı silinirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error deleting interest rate:', err);
    }
  };

  // Handle edit rate
  const handleEditRate = (rate: InterestRate) => {
    setEditingRate(rate);
    setShowEditRateModal(true);
  };

  // Update interest rate
  const updateInterestRate = async (rateData: { rate: number; term_days: number; min_amount: number; max_amount?: number; is_active: boolean }) => {
    if (!editingRate) return;
    
    setIsUpdatingRate(true);
    try {
      const updatedRate = await bankApi.rates.update(editingRate.id, rateData);
      
      // Update local state
      setInterestRates(prev => prev.map(rate => 
        rate.id === editingRate.id ? updatedRate : rate
      ));
      
      toast.success('Faiz oranı başarıyla güncellendi');
      setShowEditRateModal(false);
      setEditingRate(null);
      
    } catch (err: any) {
      const errorMessage = err.message || 'Faiz oranı güncellenirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error updating interest rate:', err);
    } finally {
      setIsUpdatingRate(false);
    }
  };

  // Handle edit bank
  const handleEditBank = (bank: Bank) => {
    setEditingBank(bank);
    setShowEditBankModal(true);
  };

  // Update bank
  const updateBank = async (bankData: { name: string; iban?: string; description?: string; website?: string; contact_info?: string; logo_url?: string }) => {
    if (!editingBank) return;
    setIsUpdatingBank(true);
    try {
      const payload = { ...bankData, logo_url: toRelativeFilesPath(bankData.logo_url) };
      const updatedBank = await bankApi.update(editingBank.id, payload);
      const updatedWithLogo = { ...updatedBank, logo_url: toBackendFileUrl(updatedBank.logo_url || bankData.logo_url) } as Bank;
      setBanks(prev => prev.map(bank => bank.id === editingBank.id ? updatedWithLogo : bank));
      toast.success('Banka başarıyla güncellendi');
      setShowEditBankModal(false);
      setEditingBank(null);
      setEditLogoUrl(undefined);
    } catch (err: any) {
      const errorMessage = err.message || 'Banka güncellenirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error updating bank:', err);
    } finally {
      setIsUpdatingBank(false);
    }
  };

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Bankalar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Hata</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            ✕
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banka Yönetimi</h1>
          <p className="text-gray-600">
            Toplam {totalBanks} banka • {banks.filter(b => b.is_active).length} aktif
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
          <Button variant="outline" onClick={showReports}>
            <BarChart3 className="w-4 h-4 mr-2" />
            Raporlar
          </Button>
          <Button variant="default" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Yeni Banka
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Toplam Banka</p>
              <p className="text-2xl font-bold text-gray-900">{totalBanks}</p>
            </div>
            <Building className="w-8 h-8 text-primary-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aktif Banka</p>
              <p className="text-2xl font-bold text-green-600">
                {banks.filter(b => b.is_active).length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Toplam Oranlar</p>
              <p className="text-2xl font-bold text-gold-700">{stats?.total_rates ?? 0}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-gold-600" />
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ort. Faiz</p>
              <p className="text-2xl font-bold text-orange-600">%{stats ? Number(stats.average_rate).toFixed(1) : '0.0'}</p>
            </div>
            <DollarSign className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Banka ara (ad, kod, website)..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Banks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {banks.map((bank) => (
          <div key={bank.id} className="bg-white rounded-xl p-6 shadow-soft border border-gray-100 hover:shadow-lg transition-shadow">
            {/* Bank Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary-400 to-gold-500">
                  {bank.logo_url ? (
                    <img src={toBackendFileUrl(bank.logo_url)} alt={bank.name} loading="lazy" className="w-12 h-12 block object-contain rounded-lg" />
                  ) : (
                    <Building className="w-6 h-6 text-white" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 truncate max-w-[180px]" title={bank.name}>
                    {bank.name.length > 25 ? `${bank.name.substring(0, 25)}...` : bank.name}
                  </h3>
                  <p className="text-sm text-gray-600">{bank.iban}</p>
                </div>
              </div>
              
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                bank.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {bank.is_active ? 'Aktif' : 'Pasif'}
              </span>
            </div>

            {/* Bank Info */}
            <div className="space-y-2 mb-4">
              {bank.website && (
                <div className="flex items-center text-sm text-gray-600">
                  <Globe className="w-4 h-4 mr-2" />
                  <span className="truncate">{bank.website}</span>
                </div>
              )}
              {bank.contact_info && (
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="truncate">{bank.contact_info}</span>
                </div>
              )}
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="w-4 h-4 mr-2" />
                <span>Eklenme: {formatDate(bank.created_at)}</span>
              </div>
            </div>

            {/* Bank Description */}
            {bank.description && (
              <div className="mb-4">
                <p className="text-sm text-gray-700 line-clamp-2">{bank.description}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => viewInterestRates(bank)}
                className="flex-1"
              >
                <TrendingUp className="w-4 h-4 mr-1" />
                Oranlar
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleEditBank(bank)}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => toggleBankStatus(bank.id)}
                className={bank.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}
              >
                {bank.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => deleteBank(bank.id)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            <span className="font-medium">{((currentPage - 1) * banksPerPage) + 1}</span>
            {' '}-{' '}
            <span className="font-medium">{Math.min(currentPage * banksPerPage, totalBanks)}</span>
            {' '}arası, toplam{' '}
            <span className="font-medium">{totalBanks}</span>
            {' '}banka
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Önceki
            </Button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {banks.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Banka bulunamadı</h3>
          <p className="text-gray-500">Arama kriterlerinize uygun banka bulunamadı.</p>
        </div>
      )}

      {/* Interest Rates Modal */}
      {showRatesModal && selectedBank && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedBank.name} - Faiz Oranları
                </h2>
                <p className="text-gray-600">Güncel faiz oranları ve koşulları</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => setShowRatesModal(false)}
              >
                ✕
              </Button>
            </div>

            {/* Interest Rates Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vade</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Oran</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min. Tutar</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Max. Tutar</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {interestRates.map((rate) => (
                    <tr key={rate.id}>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {rate.term_days} gün
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        %{rate.rate}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(rate.min_amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {rate.max_amount ? formatCurrency(rate.max_amount) : 'Sınırsız'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          rate.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {rate.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleEditRate(rate)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => toggleRateStatus(rate.id)}
                            className={rate.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}
                          >
                            {rate.is_active ? '❌' : '✅'}
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteInterestRate(rate.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {interestRates.length === 0 && (
              <div className="text-center py-8">
                <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Bu banka için henüz faiz oranı tanımlanmamış.</p>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button variant="default" onClick={() => setShowCreateRateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Yeni Oran Ekle
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Rate Modal */}
      {showCreateRateModal && selectedBank && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">{selectedBank.name} - Yeni Faiz Oranı</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              createInterestRate({
                term_days: parseInt(formData.get('term_days') as string),
                rate: parseFloat(formData.get('rate') as string),
                min_amount: parseFloat(formData.get('min_amount') as string),
                max_amount: formData.get('max_amount') ? parseFloat(formData.get('max_amount') as string) : undefined
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vade (Gün)</label>
                  <input
                    type="number"
                    name="term_days"
                    required
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: 30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yıllık Faiz Oranı (%)</label>
                  <input
                    type="number"
                    name="rate"
                    required
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: 15.50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Tutar (TL)</label>
                  <input
                    type="number"
                    name="min_amount"
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: 1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Tutar (TL) - Opsiyonel</label>
                  <input
                    type="number"
                    name="max_amount"
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: 100000 (boş bırakırsan sınırsız)"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateRateModal(false)}
                  disabled={isCreatingRate}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isCreatingRate}
                >
                  {isCreatingRate ? 'Oluşturuluyor...' : 'Oluştur'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Rate Modal */}
      {showEditRateModal && editingRate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Faiz Oranı Düzenle</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              updateInterestRate({
                rate: parseFloat(formData.get('rate') as string),
                term_days: parseInt(formData.get('term_days') as string),
                min_amount: parseFloat(formData.get('min_amount') as string),
                max_amount: formData.get('max_amount') ? parseFloat(formData.get('max_amount') as string) : undefined,
                is_active: editingRate.is_active // Keep current status
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vade (Gün)</label>
                  <input
                    type="number"
                    name="term_days"
                    required
                    min="1"
                    defaultValue={editingRate.term_days}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: 30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yıllık Faiz Oranı (%)</label>
                  <input
                    type="number"
                    name="rate"
                    required
                    min="0"
                    max="100"
                    step="0.01"
                    defaultValue={editingRate.rate}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: 15.50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Tutar (TL)</label>
                  <input
                    type="number"
                    name="min_amount"
                    required
                    min="0"
                    step="0.01"
                    defaultValue={editingRate.min_amount}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: 1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Tutar (TL) - Opsiyonel</label>
                  <input
                    type="number"
                    name="max_amount"
                    min="0"
                    step="0.01"
                    defaultValue={editingRate.max_amount || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: 100000 (boş bırakırsan sınırsız)"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditRateModal(false);
                    setEditingRate(null);
                  }}
                  disabled={isUpdatingRate}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isUpdatingRate}
                >
                  {isUpdatingRate ? 'Güncelleniyor...' : 'Güncelle'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Bank Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Yeni Banka Ekle</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              
              // Convert empty strings to undefined
              const name = formData.get('name') as string;
              const iban = formData.get('iban') as string;
              const description = formData.get('description') as string;
              const website = formData.get('website') as string;
              const contact_info = formData.get('contact_info') as string;
              
              createBank({
                name: name,
                iban: iban,  // IBAN is required
                description: description || undefined,
                website: website || undefined,
                contact_info: contact_info || undefined,
                logo_url: createLogoUrl,
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Banka Adı</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: ABC Bankası"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Banka IBAN</label>
                  <input
                    type="text"
                    name="iban"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="TR123456789012345678901234"
                  />
                </div>
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Logo (Opsiyonel)</label>
                  <FileUpload
                    acceptedTypes={['image/*']}
                    maxFiles={1}
                    allowMultiple={false}
                    compact
                    category="images"
                    onFilesUploaded={(files) => {
                      const file = files[0];
                      const preferred = (file as any).thumbnail || (file as any).url || (file as any).file?.url || '';
                      if (preferred) {
                        const absoluteForDisplay = toBackendFileUrl(preferred);
                        setCreateLogoUrl(absoluteForDisplay);
                        toast.success('Logo yüklendi');
                      }
                    }}
                  />
                  {createLogoUrl && (
                    <div className="mt-2 text-xs text-gray-500 break-all">Yüklü logo: {createLogoUrl}</div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website (Opsiyonel)</label>
                  <input
                    type="url"
                    name="website"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="https://www.example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İletişim (Opsiyonel)</label>
                  <input
                    type="email"
                    name="contact_info"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="info@skyview-assets.de"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama (Opsiyonel)</label>
                  <textarea
                    name="description"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Banka hakkında kısa açıklama..."
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isCreating}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating}
                >
                  {isCreating ? 'Oluşturuluyor...' : 'Oluştur'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Bank Modal */}
      {showEditBankModal && editingBank && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Banka Düzenle</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              updateBank({
                name: formData.get('name') as string,
                iban: formData.get('iban') as string,
                description: (formData.get('description') as string) || undefined,
                website: (formData.get('website') as string) || undefined,
                contact_info: (formData.get('contact_info') as string) || undefined,
                logo_url: editLogoUrl,
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Banka Adı</label>
                  <input
                    type="text"
                    name="name"
                    required
                    defaultValue={editingBank.name}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Örn: ABC Bankası"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Banka IBAN</label>
                  <input
                    type="text"
                    name="iban"
                    required
                    defaultValue={editingBank.iban}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="TR123456789012345678901234"
                  />
                </div>
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Logo (Opsiyonel)</label>
                  <FileUpload
                    acceptedTypes={['image/*']}
                    maxFiles={1}
                    allowMultiple={false}
                    compact
                    category="images"
                    onFilesUploaded={(files) => {
                      const file = files[0];
                      const preferred = (file as any).thumbnail || (file as any).url || (file as any).file?.url || '';
                      if (preferred) {
                        const absoluteForDisplay = toBackendFileUrl(preferred);
                        setEditLogoUrl(absoluteForDisplay);
                        toast.success('Logo yüklendi');
                      }
                    }}
                  />
                  {editLogoUrl || editingBank.logo_url ? (
                    <div className="mt-2">
                      <img src={toBackendFileUrl(editLogoUrl || (editingBank.logo_url as any))} alt="Logo" className="h-10 object-contain" />
                    </div>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Website (Opsiyonel)</label>
                  <input
                    type="url"
                    name="website"
                    defaultValue={editingBank.website || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="https://www.example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İletişim (Opsiyonel)</label>
                  <input
                    type="email"
                    name="contact_info"
                    defaultValue={editingBank.contact_info || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="info@skyview-assets.de"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama (Opsiyonel)</label>
                  <textarea
                    name="description"
                    rows={3}
                    defaultValue={editingBank.description || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Banka hakkında kısa bilgi..."
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditBankModal(false);
                    setEditingBank(null);
                    setEditLogoUrl(undefined);
                  }}
                  disabled={isUpdatingBank}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={isUpdatingBank}
                >
                  {isUpdatingBank ? 'Güncelleniyor...' : 'Güncelle'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reports Modal */}
      {showReportsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-lg font-semibold">Banka Raporları</h3>
              <Button
                variant="ghost"
                onClick={() => setShowReportsModal(false)}
              >
                ✕
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Report Cards */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <BarChart3 className="w-6 h-6 text-primary-600 mr-3" />
                  <h4 className="font-medium">Genel İstatistikler</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">Tüm bankaların genel performans raporu</p>
                <Button size="sm" variant="outline" className="w-full">
                  Raporu Görüntüle
                </Button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <TrendingUp className="w-6 h-6 text-green-600 mr-3" />
                  <h4 className="font-medium">Faiz Oranları</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">Bankaların faiz oranları karşılaştırması</p>
                <Button size="sm" variant="outline" className="w-full">
                  Raporu Görüntüle
                </Button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Activity className="w-6 h-6 text-primary-600 mr-3" />
                  <h4 className="font-medium">Aktivite Raporu</h4>
                </div>
                <p className="text-sm text-gray-600 mb-3">Banka ekleme/güncelleme aktiviteleri</p>
                <Button size="sm" variant="outline" className="w-full">
                  Raporu Görüntüle
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankManagement;